import React, { useEffect, useState } from 'react';
import { transactionTemplatesService } from '../../services/transactionTemplates.service';
import { conditionLabels, countTemplate, partyRoleLabel } from '../../utils/transactionTemplates';
import './TemplatePreviewCard.css';

const SALE_TYPE_LABELS = { STANDARD: 'Standard', PROBATE: 'Probate', REO: 'REO', TRUST: 'Trust' };

/**
 * Read-only preview of the template picked on the create-transaction pages.
 * Its body scrolls inside a fixed height so a long template never pushes the
 * page's Create / Cancel buttons out of view.
 */
const TemplatePreviewCard = ({ templateId, saleType, primaryAgentId }) => {
  const [template, setTemplate] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!templateId) { setTemplate(null); return undefined; }
    let cancelled = false;
    setTemplate(null);
    setError(false);
    transactionTemplatesService.getTemplate(templateId, { primaryAgentId })
      .then((t) => { if (!cancelled) setTemplate(t); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [templateId, primaryAgentId]);

  if (!templateId) return null;

  // Same rule the backend applies: a Rialty item is left out when this sale type is exempt.
  const isExcluded = (item) => !!saleType && !!item.condition?.excludeSaleTypes?.includes(saleType);
  const counts = template ? countTemplate(template) : null;
  const includedCount = template ? template.items.filter((i) => !isExcluded(i)).length : 0;

  return (
    <section className="tpl-preview-card" aria-label="Template preview">
      <div className="tpl-preview-header">
        <div>
          <h3 className="tpl-preview-title">Template preview{template ? `: ${template.name}` : ''}</h3>
          {counts && (
            <p className="tpl-preview-summary">
              {includedCount} documents · {counts.partyCount} parties
            </p>
          )}
        </div>
      </div>

      <div className="tpl-preview-body">
        {error && <p className="tpl-preview-empty">Couldn't load the template preview.</p>}
        {!error && !template && <p className="tpl-preview-empty">Loading preview…</p>}

        {template && (
          <>
            {template.folders.map((folder) => {
              const items = template.items.filter((i) => i.folder === folder.slug);
              if (items.length === 0) return null;
              return (
                <div key={folder.slug} className="tpl-preview-group">
                  <div className="tpl-preview-group-title">📂 {folder.name}</div>
                  <ul className="tpl-preview-list">
                    {items.map((item) => {
                      const excluded = isExcluded(item);
                      const notes = conditionLabels(item.condition).filter((label) => !label.startsWith('Not required for'));
                      return (
                        <li key={item.key} className={`tpl-preview-row${excluded ? ' is-excluded' : ''}`}>
                          <div className="tpl-preview-row-main">
                            <span className="tpl-preview-row-name">{item.name}</span>
                            <span className="tpl-preview-row-note">
                              {excluded
                                ? `Not included for ${SALE_TYPE_LABELS[saleType] || saleType} sale`
                                : [item.type === 'FORM' ? `Form · ${item.form?.code}` : '⬆ Pending upload', ...notes].join(' · ')}
                            </span>
                          </div>
                          {!excluded && (
                            <span className={`tpl-preview-tag ${item.mandatory ? 'is-mandatory' : 'is-optional'}`}>
                              {item.mandatory ? 'Mandatory' : 'Optional'}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}

            {template.parties.length > 0 && (
              <div className="tpl-preview-group">
                <div className="tpl-preview-group-title">👥 Parties (to be invited)</div>
                <div className="tpl-preview-chips">
                  {template.parties.map((role) => <span key={role} className="tpl-preview-chip">{partyRoleLabel(role)}</span>)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default TemplatePreviewCard;
